import { supabaseData } from './supabaseData'

const API_BASE = `${(import.meta as any).env?.VITE_SUPABASE_URL}/functions/v1`

export interface RequestOptions extends RequestInit {
  skipAuth?: boolean
  requireAuth?: boolean
}

export interface Estimate {
  id: string
  title: string
  clientId: string
  totalAmount: number
  status: 'draft' | 'sent' | 'viewed' | 'accepted' | 'declined' | 'scheduled'
  createdAt: string
  validUntil: string
}

export interface QuickBooksStatus {
  connected: boolean
  connectionId?: string
  realmId?: string
  companyName?: string
  lastSync?: string
  lastSyncStatus?: string
  lastSyncError?: string
  syncSettings?: {
    syncCustomers: boolean
    syncInvoices: boolean
    syncPayments: boolean
    syncExpenses: boolean
    autoSyncOnWrite: boolean
  }
  mappings?: {
    defaultIncomeAccountId?: string | null
    defaultIncomeAccountName?: string | null
    defaultItemId?: string | null
    defaultItemName?: string | null
    defaultExpenseAccountId?: string | null
    defaultExpenseAccountName?: string | null
    defaultPaymentDepositAccountId?: string | null
    defaultPaymentDepositAccountName?: string | null
  }
  recentLogs?: any[]
  error?: string
}

export interface QuickBooksSettingsPatch {
  syncCustomers?: boolean
  syncInvoices?: boolean
  syncPayments?: boolean
  syncExpenses?: boolean
  autoSyncOnWrite?: boolean
  defaultIncomeAccountId?: string | null
  defaultIncomeAccountName?: string | null
  defaultItemId?: string | null
  defaultItemName?: string | null
  defaultExpenseAccountId?: string | null
  defaultExpenseAccountName?: string | null
  defaultPaymentDepositAccountId?: string | null
  defaultPaymentDepositAccountName?: string | null
}

export interface QuickBooksAccount {
  id: string
  name: string
  accountType: string
  accountSubType?: string
  classification?: string
}

export interface QuickBooksItem {
  id: string
  name: string
  type?: string
  incomeAccountId?: string
}

export interface QuickBooksSyncSummary {
  ok: boolean
  summary?: {
    clients: { ok: number; fail: number; skipped: number; errors: Array<{ id: string; message: string }> }
    invoices: { ok: number; fail: number; skipped: number; errors: Array<{ id: string; message: string }> }
    expenses: { ok: number; fail: number; skipped: number; errors: Array<{ id: string; message: string }> }
  }
}

class ApiClient {
  // Circuit breaker properties
  private failureCount = 0
  private lastFailureTime = 0
  private readonly FAILURE_THRESHOLD = 3
  private readonly RESET_TIME = 5000 // 5 seconds
  private authCache: { result: { isAuthenticated: boolean; token: string | null }; timestamp: number } | null = null
  
  // Supabase client instance
  private supabase: any = null
  
  constructor() {
    this.initializeSupabase()
  }
  
  private async initializeSupabase() {
    try {
      // Import Supabase client dynamically to avoid circular dependencies
      const { supabase } = await import('./supabase.js')
      this.supabase = supabase
    } catch (error) {
      console.error('Failed to initialize Supabase client:', error)
      // Supabase client will remain null, but we'll handle this in methods
    }
  }

  private async ensureSupabaseInitialized() {
    if (!this.supabase) {
      await this.initializeSupabase()
      if (!this.supabase) {
        throw new Error('Supabase client not initialized. Please refresh the page.')
      }
    }
  }
  
  private async getAuthHeaders(): Promise<HeadersInit> {
    // Check if Supabase environment variables are configured
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL
    if (!supabaseUrl) {
      // Check for development mode mock token
      if (import.meta.env.DEV) {
        const mockToken = localStorage.getItem('dev_mock_token')
        if (mockToken) {
          return {
            'Authorization': `Bearer ${mockToken}`,
            'Content-Type': 'application/json'
          }
        }
      }
      
      // Fallback to old localStorage method
      const token = localStorage.getItem('token')
      if (!token) {
        return {
          'Content-Type': 'application/json'
        }
      }
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
    
    // Use Supabase client to get current session
    try {
      // Import supabase client dynamically to avoid circular dependencies
      const { supabase } = await import('./supabase.js')
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error) {
        console.warn('Failed to get Supabase session:', error)
        return {
          'Content-Type': 'application/json'
        }
      }
      
      if (session?.access_token) {
        return {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      }
    } catch (error) {
      console.warn('Failed to get Supabase session in getAuthHeaders:', error)
    }
    
    // Fallback to old localStorage method
    const token = localStorage.getItem('token')
    if (!token) {
      return {
        'Content-Type': 'application/json'
      }
    }
      
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  }

  /**
   * Get headers including the authenticated user's company ID
   * SECURITY: Company ID is fetched from the authenticated session, not hardcoded
   * This prevents company ID spoofing from the client side
   */
  private async getCompanyHeaders(): Promise<HeadersInit> {
    const authHeaders = await this.getAuthHeaders()
    
    // Get company ID from the authenticated user's session
    let companyId: string | null = null
    
    try {
      await this.ensureSupabaseInitialized()
      const { data: { user } } = await this.supabase.auth.getUser()
      
      if (user) {
        // First check user metadata (fast path - cached by Supabase)
        if (user.user_metadata?.company_id) {
          companyId = user.user_metadata.company_id
        } else {
          // Fallback to user_access_cache lookup
          const { getCompanyId } = await import('./supabase.js')
          companyId = await getCompanyId(user)
        }
      }
    } catch (error) {
      console.warn('[apiClient] Failed to get company ID for headers:', error)
    }
    
    // Only include X-Company-Id header if we have a valid company ID
    // Note: Server-side still validates this against the authenticated user
    if (companyId) {
      return {
        ...authHeaders,
        'X-Company-Id': companyId
      }
    }
    
    return authHeaders
  }

  private async checkAuthStatus(): Promise<{ isAuthenticated: boolean; token: string | null }> {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return { isAuthenticated: false, token: null }
    }
    
    // Add caching to prevent duplicate calls
    const now = Date.now()
    if (this.authCache && (now - this.authCache.timestamp) < 5000) { // 5 second cache
      return this.authCache.result
    }
    
    // Check for Supabase session using the client
    try {
      const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL
      
      if (supabaseUrl) {
        // Use Supabase client to get session
        await this.ensureSupabaseInitialized()
        const { data: { session } } = await this.supabase.auth.getSession()
        
        const hasSupabaseSession = !!(session && session.access_token)
        
        const result = {
          isAuthenticated: hasSupabaseSession,
          token: session?.access_token || null
        }
        this.authCache = { result, timestamp: now }
        return result
      }
      
      // Fallback to old localStorage method if no Supabase URL
      const token = localStorage.getItem('token')
      const result = {
        isAuthenticated: !!token,
        token: token
      }
      this.authCache = { result, timestamp: now }
      return result
    } catch (error) {
      console.warn('[API] Failed to check auth status:', error)
      // Fallback to false
      const result = {
        isAuthenticated: false,
        token: null
      }
      this.authCache = { result, timestamp: now }
      return result
    }
  }

  private handleAuthError(error: any): never {
    // Clear invalid credentials
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    
    // Also clear Supabase session if it exists
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL
    if (supabaseUrl) {
      const supabaseKey = 'sb-' + supabaseUrl.replace(/[^a-zA-Z0-9]/g, '') + '-auth-token'
      localStorage.removeItem(supabaseKey)
    }
    
    // Don't redirect - let React handle the state change
    // This prevents infinite loops
    console.warn('Authentication error handled:', error.message)
    
    throw new Error('Authentication required. Please log in again.')
  }

  async request<T = any>(url: string, options: RequestOptions = {}): Promise<T> {
    // Circuit breaker check
    if (this.failureCount >= this.FAILURE_THRESHOLD) {
      const timeSinceLastFailure = Date.now() - this.lastFailureTime
      if (timeSinceLastFailure < this.RESET_TIME) {
        console.warn(`API circuit breaker activated for ${url} - too many failures (${this.failureCount})`)
        throw new Error('API circuit breaker activated - too many failures. Please try again later.')
      }
      // Reset circuit breaker after reset time has passed
      console.log(`API circuit breaker reset for ${url} after ${this.RESET_TIME}ms`)
      this.failureCount = 0
    }
    
    const { skipAuth, requireAuth, ...fetchOptions } = options
    
    const headers = skipAuth 
      ? { 'Content-Type': 'application/json' }
      : await this.getAuthHeaders()
    
    // For endpoints that explicitly require auth, verify Authorization header is present
    if (requireAuth && !headers['Authorization']) {
      this.handleAuthError(new Error('Authentication required'))
    }
    
    // Safety check: don't make authenticated requests without a token
    if (!skipAuth && !url.startsWith('/auth/') && !url.startsWith('/health') && !url.startsWith('/public/')) {
      if (!headers['Authorization']) {
        console.warn('Attempted API call without authentication:', url)
        // Don't redirect - just throw an error
        throw new Error('Authentication required for this endpoint')
      }
    }

    try {
      const response = await fetch(`${API_BASE}${url}`, {
        ...fetchOptions,
        headers: {
          ...headers,
          ...fetchOptions.headers
        }
      })

      // Handle authentication errors
      if (response.status === 401 && !skipAuth) {
        // Only clear tokens for specific auth endpoints
        if (url.includes('/auth/verify')) {
          console.warn('API returned 401 for auth verification - clearing tokens')
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          
          // Also clear Supabase session
          const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL
          if (supabaseUrl) {
            const supabaseKey = 'sb-' + supabaseUrl.replace(/[^a-zA-Z0-9]/g, '') + '-auth-token'
            localStorage.removeItem(supabaseKey)
          }
        } else {
          console.warn('API returned 401 for non-auth endpoint - not clearing tokens')
        }
        throw new Error('Authentication expired. Please log in again.')
      }

      if (response.status === 403) {
        throw new Error('Access denied. Insufficient permissions.')
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || `Request failed: ${response.statusText}`)
      }

      // Handle empty responses
      const text = await response.text()
      if (!text) return {} as T
      
      // Reset circuit breaker on successful request
      if (this.failureCount > 0) {
        console.log(`API success for ${url} - resetting circuit breaker failure count`)
        this.failureCount = 0
      }
      
      try {
        return JSON.parse(text)
      } catch {
        return text as unknown as T
      }
    } catch (error) {
      console.error('API request failed:', url, error)
      
      // Update circuit breaker failure count
      this.failureCount++
      this.lastFailureTime = Date.now()
      console.warn(`API failure count for ${url}: ${this.failureCount}/${this.FAILURE_THRESHOLD}`)
      
      // Handle specific error types
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your connection and try again.')
      }
      
      // Handle CORS errors
      if (error instanceof TypeError && error.message.includes('CORS')) {
        throw new Error('CORS error. Please check your domain configuration.')
      }
      
      // Handle authentication errors
      if (error.message && error.message.includes('401')) {
        this.handleAuthError(error)
      }
      
      // Handle server errors
      if (error.message && error.message.includes('500')) {
        throw new Error('Server error. Please try again later.')
      }
      
      throw error
    }
  }

  // Generic HTTP methods
  get<T = any>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, { ...options, method: 'GET' })
  }

  post<T = any>(url: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  put<T = any>(url: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  delete<T = any>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, { ...options, method: 'DELETE' })
  }

  // Estimate endpoints - using direct database calls with RLS protection
  async getEstimates(): Promise<Estimate[]> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }

    // Ensure Supabase client is initialized
    await this.ensureSupabaseInitialized()

    const { data, error } = await this.supabase
      .from('estimates')
      .select(`
        *,
        clients (
          id,
          name,
          email,
          phone
        )
      `)
      .order('created_at', { ascending: false })

    if (error) {
      throw new Error(error.message)
    }

    return data || []
  }

  async createEstimate(estimateData: Omit<Estimate, 'id' | 'createdAt' | 'updatedAt'>): Promise<Estimate> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    
    // Ensure Supabase client is initialized
    await this.ensureSupabaseInitialized()

    const { data: user } = await this.supabase.auth.getUser()
    if (!user.user) {
      throw new Error('User not found')
    }

    const { data, error } = await this.supabase
      .from('estimates')
      .insert({
        ...estimateData,
        created_by: user.user.id
      })
      .select(`
        *,
        clients (
          id,
          name,
          email,
          phone
        )
      `)
      .single()

    if (error) {
      throw new Error(error.message)
    }

    return data
  }

  async updateEstimate(estimateId: string, estimateData: Partial<Estimate>): Promise<Estimate> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    
    // Ensure Supabase client is initialized
    await this.ensureSupabaseInitialized()

    const { data, error } = this.supabase
      .from('estimates')
      .update({
        ...estimateData,
        updated_at: new Date().toISOString()
      })
      .eq('id', estimateId)
      .select(`
        *,
        clients (
          id,
          name,
          email,
          phone
        )
      `)
      .single()

    if (error) {
      throw new Error(error.message)
    }

    return data
  }


  // Integration endpoints - QuickBooks OAuth
  async getQuickBooksStatus(): Promise<QuickBooksStatus> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    const headers = await this.getCompanyHeaders()
    return this.get(`/quickbooks-oauth?action=status`, { headers })
  }

  async connectQuickBooks(): Promise<{ connectUrl: string }> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    const headers = await this.getCompanyHeaders()
    return this.get(`/quickbooks-oauth?action=connect`, { headers })
  }

  async refreshQuickBooksToken(): Promise<void> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    const headers = await this.getCompanyHeaders()
    return this.post(`/quickbooks-oauth?action=refresh`, {}, { headers })
  }

  async disconnectQuickBooks(): Promise<void> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    const headers = await this.getCompanyHeaders()
    return this.post(`/quickbooks-oauth?action=disconnect`, {}, { headers })
  }

  async syncQuickBooks(): Promise<QuickBooksSyncSummary> {
    return this.syncQuickBooksAll()
  }

  async syncQuickBooksAll(): Promise<QuickBooksSyncSummary> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    const headers = await this.getCompanyHeaders()
    return this.post(`/quickbooks-sync`, { action: 'all' }, { headers })
  }

  async syncQuickBooksEntity(
    entityType: 'client' | 'invoice' | 'expense',
    entityId: string,
  ): Promise<{ ok: boolean; qbId?: string; skipped?: boolean; reason?: string; error?: string }> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    const headers = await this.getCompanyHeaders()
    return this.post(
      `/quickbooks-sync`,
      { action: entityType, entityId },
      { headers },
    )
  }

  async updateQuickBooksSettings(patch: QuickBooksSettingsPatch): Promise<{ ok: boolean }> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    const headers = await this.getCompanyHeaders()
    return this.post(`/quickbooks-oauth?action=settings`, patch, { headers })
  }

  async getQuickBooksAccounts(kind?: 'income' | 'expense' | 'bank'): Promise<QuickBooksAccount[]> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    const headers = await this.getCompanyHeaders()
    const query = kind ? `&kind=${kind}` : ''
    const response: { accounts: QuickBooksAccount[] } = await this.get(
      `/quickbooks-metadata?action=accounts${query}`,
      { headers },
    )
    return response.accounts || []
  }

  async getQuickBooksItems(): Promise<QuickBooksItem[]> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    const headers = await this.getCompanyHeaders()
    const response: { items: QuickBooksItem[] } = await this.get(
      `/quickbooks-metadata?action=items`,
      { headers },
    )
    return response.items || []
  }


  // Estimate endpoints
  async publishEstimate(estimateId: string): Promise<any> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    
    if (!this.supabase) {
      throw new Error('Supabase client not initialized. Please refresh the page.')
    }

    // Use direct database update to publish estimate
    const { data, error } = await this.supabase
      .from('estimates')
      .update({
        status: 'published',
        published_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', estimateId)
      .select(`
        *,
        clients (
          id,
          name,
          email,
          phone
        )
      `)
      .single()

    if (error) {
      throw new Error(error.message)
    }

    return data
  }

  async deleteEstimate(estimateId: string): Promise<void> {
    const authStatus = await this.checkAuthStatus()
    if (!authStatus.isAuthenticated || !authStatus.token) {
      return Promise.reject(new Error('Authentication required'))
    }
    
    if (!this.supabase) {
      throw new Error('Supabase client not initialized. Please refresh the page.')
    }

    // Use direct database deletion with RLS protection
    const { error } = await this.supabase
      .from('estimates')
      .delete()
      .eq('id', estimateId)

    if (error) {
      throw new Error(error.message)
    }
  }


  // Circuit breaker management methods
  getCircuitBreakerStatus(): { failureCount: number; lastFailureTime: number; isOpen: boolean; timeUntilReset: number } {
    const timeSinceLastFailure = Date.now() - this.lastFailureTime
    const isOpen = this.failureCount >= this.FAILURE_THRESHOLD && timeSinceLastFailure < this.RESET_TIME
    const timeUntilReset = Math.max(0, this.RESET_TIME - timeSinceLastFailure)
    
    return {
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime,
      isOpen,
      timeUntilReset
    }
  }

  resetCircuitBreaker(): void {
    console.log('Manually resetting API circuit breaker')
    this.failureCount = 0
    this.lastFailureTime = 0
  }


}

// Create structured API object to match component expectations
const createStructuredApi = (client: ApiClient) => ({
  // Jobs API
  jobs: {
    create: async (jobData: any) => {
      try {
        const result = await supabaseData.createJob(jobData)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    update: async (jobId: string, updates: any) => {
      try {
        const result = await supabaseData.updateJob(jobId, updates)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    delete: async (jobId: string) => {
      try {
        await supabaseData.deleteJob(jobId)
        return { success: true }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    // Additional job methods that components expect
    createChecklist: async (jobId: string) => {
      try {
        // Import the actual function from the jobs module
        const { createChecklist } = await import('./api/jobs')
        const result = await createChecklist(jobId)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    addChecklistItem: async (data: any) => {
      try {
        const { addChecklistItem } = await import('./api/jobs')
        const result = await addChecklistItem(data)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    updateChecklistItem: async (data: any) => {
      try {
        const { updateChecklistItem } = await import('./api/jobs')
        const result = await updateChecklistItem(data)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    toggleChecklistItem: async (data: any) => {
      try {
        const { toggleChecklistItem } = await import('./api/jobs')
        const result = await toggleChecklistItem(data)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    deleteChecklistItem: async (itemId: string) => {
      try {
        const { deleteChecklistItem } = await import('./api/jobs')
        const result = await deleteChecklistItem(itemId)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    batchUpdateChecklistItems: async (updates: any[]) => {
      try {
        const { batchUpdateChecklistItems } = await import('./api/jobs')
        const result = await batchUpdateChecklistItems(updates)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    batchToggleChecklistItems: async (toggles: any[]) => {
      try {
        const { batchToggleChecklistItems } = await import('./api/jobs')
        const result = await batchToggleChecklistItems(toggles)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    batchDeleteChecklistItems: async (deletes: string[]) => {
      try {
        const { batchDeleteChecklistItems } = await import('./api/jobs')
        const result = await batchDeleteChecklistItems(deletes)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    uploadChecklistImage: async (data: any) => {
      try {
        const { uploadChecklistImage } = await import('./api/jobs')
        const result = await uploadChecklistImage(data)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    deleteChecklistAttachment: async (attachmentId: string) => {
      try {
        const { deleteChecklistAttachment } = await import('./api/jobs')
        const result = await deleteChecklistAttachment(attachmentId)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    addJobActivity: async (jobId: string, data: any) => {
      try {
        const { addJobActivity } = await import('./api/jobs')
        const result = await addJobActivity(jobId, data)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    updateJobActivityNote: async (activityId: string, note: string) => {
      try {
        const { updateJobActivityNote } = await import('./api/jobs')
        const result = await updateJobActivityNote(activityId, note)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    deleteJobActivity: async (activityId: string) => {
      try {
        const { deleteJobActivity } = await import('./api/jobs')
        const result = await deleteJobActivity(activityId)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    deleteJobMedia: async (mediaId: string) => {
      try {
        const { deleteJobMedia } = await import('./api/jobs')
        const result = await deleteJobMedia(mediaId)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    cleanupJobMedia: async (jobId: string) => {
      try {
        const { deleteJobMedia } = await import('./api/jobs')
        // This would need to be implemented to clean up all media for a job
        return { success: true, data: null }
      } catch (error) {
        return { success: false, error: error.message }
      }
    }
  },
  
  // Estimates API
  estimates: {
    create: async (estimateData: any) => {
      try {
        const result = await client.createEstimate(estimateData)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    update: async (estimateId: string, updates: any) => {
      try {
        const result = await client.updateEstimate(estimateId, updates)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    delete: async (estimateId: string) => {
      try {
        const result = await client.deleteEstimate(estimateId)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    },
    publish: async (estimateId: string) => {
      try {
        const result = await client.publishEstimate(estimateId)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    }
  },
  
  // Automations API
  automations: {
    scheduleJobAutomations: async (jobId: string, scheduledAt: string) => {
      try {
        const { scheduleJobAutomations } = await import('./api/automation')
        const result = await scheduleJobAutomations(jobId, scheduledAt)
        return { success: true, data: result }
      } catch (error) {
        return { success: false, error: error.message }
      }
    }
  },
  
  // Direct access to the underlying client methods - estimates
  createEstimate: client.createEstimate.bind(client),
  updateEstimate: client.updateEstimate.bind(client),
  deleteEstimate: client.deleteEstimate.bind(client),
  getEstimates: client.getEstimates.bind(client),

  // QuickBooks - explicitly bind to ensure prototype methods are reachable through the spread.
  getQuickBooksStatus: client.getQuickBooksStatus.bind(client),
  connectQuickBooks: client.connectQuickBooks.bind(client),
  refreshQuickBooksToken: client.refreshQuickBooksToken.bind(client),
  disconnectQuickBooks: client.disconnectQuickBooks.bind(client),
  syncQuickBooks: client.syncQuickBooks.bind(client),
  syncQuickBooksAll: client.syncQuickBooksAll.bind(client),
  syncQuickBooksEntity: client.syncQuickBooksEntity.bind(client),
  updateQuickBooksSettings: client.updateQuickBooksSettings.bind(client),
  getQuickBooksAccounts: client.getQuickBooksAccounts.bind(client),
  getQuickBooksItems: client.getQuickBooksItems.bind(client),

  // Direct access to the underlying client for other methods
  ...client
})

const apiClient = new ApiClient()
export const api = createStructuredApi(apiClient)
export default api

